<template>
	<div class="overlay" @contextmenu.prevent @click="closeModal">
		<button class="close-button" @click="closeModal">✖</button>
		<div class="video-container">
			<div class="block-overlay-top" @click.stop></div>

			<template v-if="!isPlaying && isMobile">
				<!-- 모바일에서만 플레이 버튼 표시 -->
				<div class="play-container" @click.stop="startPlayback" @touchstart.stop="startPlayback">
					<div class="play-button">
						<svg viewBox="0 0 24 24" fill="white">
							<path d="M8 5v14l11-7z" />
						</svg>
					</div>
					<p class="play-text">동영상 재생하기</p>
				</div>
			</template>

			<template v-else>
				<!-- PC 또는 재생 시작 후 iframe 표시 -->
				<div :class="{ 'iframe-container': !isMobile, 'iframe-container-mobile': isMobile }">
					<iframe
						ref="youtubeIframe"
						:src="actualVideoSrc"
						allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
						allowfullscreen
						playsinline
						webkitplaysinline
					></iframe>
				</div>
			</template>

			<div class="block-overlay-bottom" @click.stop></div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

const props = defineProps<{
	videoSrc: string;
}>();
const emit = defineEmits(['click-close']);

const youtubeIframe = ref<HTMLIFrameElement | null>(null);
const isPlaying = ref(false);
const isMobile = ref(false);

// 모바일 환경 감지
onMounted(() => {
	isMobile.value = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

	// PC 환경에서는 자동으로 재생 상태로 설정
	if (!isMobile.value) {
		isPlaying.value = true;
	}
});

// YouTube ID 추출
const youtubeId = computed(() => {
	let videoId = '';

	// /embed/ 형식 URL
	if (props.videoSrc.includes('/embed/')) {
		const match = props.videoSrc.match(/\/embed\/([^/?]+)/);
		if (match) videoId = match[1];
	}
	// v= 형식 URL
	else if (props.videoSrc.includes('v=')) {
		const match = props.videoSrc.match(/[?&]v=([^&]+)/);
		if (match) videoId = match[1];
	}
	// youtu.be 형식 URL
	else if (props.videoSrc.includes('youtu.be/')) {
		const match = props.videoSrc.match(/youtu\.be\/([^?&]+)/);
		if (match) videoId = match[1];
	}

	return videoId;
});

// 실제 사용할 video URL (자동 재생 파라미터 포함)
const actualVideoSrc = computed(() => {
	if (!youtubeId.value) return props.videoSrc;

	// 기기에 따라 다른 파라미터 적용
	const autoplayParam = isMobile.value ? '1' : '1';
	return `https://www.youtube.com/embed/${youtubeId.value}?autoplay=${autoplayParam}&playsinline=1&controls=0&rel=0&modestbranding=1`;
});

// 모바일에서만 호출되는 재생 시작 함수
const startPlayback = () => {
	// 모바일에서만 이 함수가 호출됨
	if (isMobile.value) {
		isPlaying.value = true;
	}
};

const closeModal = () => {
	emit('click-close');
};
</script>

<style scoped lang="scss">
.overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.8);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 9999;
	cursor: pointer;
}

.video-container {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	position: relative;
	width: 100vw;
	height: 56.25vw;
	max-height: 100%;
}

.iframe-container {
	width: 100%;
	height: 100%;
	max-width: 128rem;
	max-height: 72rem;
	pointer-events: none; /* PC에서 클릭 이벤트 비활성화 */
}

/* 모바일에서는 iframe과 상호작용 가능하도록 설정 */
.iframe-container-mobile {
	width: 100%;
	height: 100%;
	max-width: 128rem;
	max-height: 72rem;
	pointer-events: auto; /* 모바일에서는 클릭 이벤트 활성화 */
}

iframe {
	width: 100%;
	height: 100%;
	border: none;
}

.block-overlay-top,
.block-overlay-bottom {
	position: absolute;
	width: 100vw;
	height: 33.3%;
	max-width: 128rem;
}

.block-overlay-top {
	top: 0rem;
}

.block-overlay-bottom {
	bottom: 0rem;
}

.play-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	max-width: 128rem;
	max-height: 72rem;
	background-color: rgba(0, 0, 0, 0.4);
	cursor: pointer;
}

.play-text {
	margin-top: 15px;
	color: white;
	font-size: 16px;
	font-weight: bold;
}

.play-button {
	width: 80px;
	height: 80px;
	background-color: rgba(255, 0, 0, 0.8);
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;
}

svg {
	width: 50px;
	height: 50px;
}

.close-button {
	position: absolute;
	top: 2rem;
	right: 2rem;
	background: rgba(0, 0, 0, 0.6);
	color: white;
	border: none;
	border-radius: 50%;
	width: 2.4rem;
	height: 2.4rem;
	font-size: 2rem;
	cursor: pointer;
	display: flex;
	justify-content: center;
	align-items: center;
}

.close-button:hover {
	background: rgba(0, 0, 0, 0.8);
}
</style>
